<template>
  <v-card>
    <!-- Search Bar -->
    <v-card-title>
      <v-row justify="end">
        <v-col cols="12" md="6">
          <div class="d-flex mb-5">
            <v-text-field
              v-model.trim="searchText"
              label="Search name, email, testator ID or ID number"
              single-line
              hide-details
              :append-icon="icons.mdiMagnify"
              @click:append="searchData()"
              @click:clear="clearSearch()"
              @keydown.enter.prevent="searchData()"
              @blur="searchData()"
            />
          </div>
        </v-col>
      </v-row>
    </v-card-title>

    <!-- Stage Dropdown Selector -->
    <div class="mobile-stage-select">
      <ListSelect
        :items="filterTabs"
        :value="activeTab"
        label="Stage"
        @input="onTabSelect"
      />
    </div>

    <!-- Data Table -->
    <v-data-table
      class="admin-list-table"
      :headers="headers"
      :items="filteredTestators"
      :loading="loading || $apollo.loading"
      :options.sync="options"
      :server-items-length="totalTestators"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 100, 500],
      }"
      :mobile-breakpoint="0"
      @click:row="redirectToSummary"
    >
      <template #[`item.fullName`]="{ item }">
        {{ item.firstNames }} {{ item.lastName }}
      </template>
      <template
        v-if="activeTab !== 'in_storage'"
        #[`item.nextRequiredAction`]="{ item }"
      >
        <div class="action-row">
          <div class="icon-wrapper">
            <v-img
              :src="
                require(`@/assets/shared/advisor-dashboard/icons/${getActionIcon(
                  item.nextRequiredAction
                )}`)
              "
              width="20"
              height="20"
              contain
            />
          </div>
          <span class="action-text">{{ item.nextRequiredAction }}</span>
        </div>
      </template>
      <template
        v-if="activeTab === 'in_storage'"
        #[`item.dateStored`]="{ item }"
      >
        <span>{{ item.vaultStored | date_DMMMYYYY }}</span>
      </template>
      <template
        v-if="activeTab === 'validated_wills'"
        #[`item.willCollection`]="{ item }"
      >
        <v-chip v-if="item.willReadyToCollect" color="green" x-small dark>
          Collection booked
        </v-chip>
        <span v-else class="text-caption text--secondary">
          Ready to Schedule Collection
        </span>
      </template>
    </v-data-table>
    <!--
      Temporarily hidden (OMW-433): the bulk booking books a separate Courier
      Guy collection per testator at the same address. Re-enable once the
      shared-collection version of the booking flow is live.
    -->
    <GradientButton
      v-if="activeTab === 'validated_wills' && canBookCollection"
      class="mt-5 mb-5 ml-8"
      :title="'Book Collection'"
      @gradientButtonClicked="openCollectionDialog"
    />

    <WillCollectionBookingDialog
      v-model="collectionDialogOpen"
      mode="advisor"
      @booking-success="onCollectionBooked"
    />

    <DryRunRequestsDialog
      v-model="dryRunPreviewOpen"
      :requests="dryRunPreviewRequests"
    />

    <v-snackbar
      v-model="collectionBookedSnackbar"
      color="success"
      :timeout="6000"
    >
      {{ collectionBookedMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
  import {
    ADVISOR_TESTATORS_OPTIMIZED_QUERY,
    ADVISOR_TESTATOR_ACTION_COUNTS_OPTIMIZED_QUERY,
    ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
  } from '../../constants/graphql/testators';
  import ListSelect from '../common/components/ListSelect.vue';
  import WillCollectionBookingDialog from '../common/will_collection_bookings/WillCollectionBookingDialog.vue';
  import DryRunRequestsDialog from '../common/will_collection_bookings/DryRunRequestsDialog.vue';
  import { whenNoDialogIsActive } from '../../helpers/dialogStacking';

  export default {
    name: 'AdvisorDatatableMobileOptimized',
    components: {
      ListSelect,
      WillCollectionBookingDialog,
      DryRunRequestsDialog,
    },
    props: {
      company: {
        type: Object,
        default: () => {
          return {};
        },
      },
      searchAdvisorEmail: {
        type: String,
        default: null,
      },
      activeTab: {
        type: String,
        default: 'list_all',
      },
    },
    data() {
      return {
        searchText: '',
        allTestators: [],
        loading: true,
        options: {},
        totalTestators: 0,
        searchQueryParam: '',
        collectionDialogOpen: false,
        BOOK_COLLECTION_ENABLED: false,
        collectionBookedSnackbar: false,
        collectionBookedDryRun: false,
        collectionBookedLinked: false,
        eligibleCollectionTestators: [],
        collectionBookedCount: 0,
        dryRunPreviewOpen: false,
        dryRunPreviewRequests: '',
        actionCountsData: {
          complete_steps: 0,
          download_wills: 0,
          sign_upload_wills: 0,
          validated_wills: 0,
          in_storage: 0,
        },
        filterTabs: [
          { text: 'List all', value: 'list_all' },
          { text: 'Complete Steps', value: 'complete_steps' },
          { text: 'Download Wills', value: 'download_wills' },
          { text: 'Sign and Upload Wills', value: 'sign_upload_wills' },
          { text: 'Validated Wills', value: 'validated_wills' },
          { text: 'In Storage', value: 'in_storage' },
        ],
      };
    },
    computed: {
      headers() {
        if (this.activeTab === 'in_storage') {
          return [
            {
              text: 'Full Name',
              value: 'fullName',
              sortable: true,
              width: '50%',
              fixed: true,
            },
            {
              text: 'Date Stored',
              value: 'dateStored',
              sortable: false,
              width: '50%',
              fixed: true,
            },
          ];
        }
        if (this.activeTab === 'validated_wills') {
          return [
            {
              text: 'Full Name',
              value: 'fullName',
              sortable: true,
              width: '50%',
              fixed: true,
            },
            {
              text: 'Wills in my possession',
              value: 'willCollection',
              sortable: false,
              align: 'center',
              width: '50%',
              fixed: true,
            },
          ];
        }
        return [
          {
            text: 'Full Name',
            value: 'fullName',
            sortable: true,
            width: '50%',
            fixed: true,
          },
          {
            text: 'Next required action',
            value: 'nextRequiredAction',
            sortable: this.activeTab === 'list_all',
            width: '50%',
            fixed: true,
          },
        ];
      },
      filteredTestators() {
        return this.allTestators;
      },
      actionCounts() {
        return this.actionCountsData;
      },
      canBookCollection() {
        return this.eligibleCollectionTestators.length > 0;
      },
      collectionBookedMessage() {
        const count = this.collectionBookedCount;
        const wills = count === 1 ? '1 will' : `${count} wills`;
        if (this.collectionBookedLinked) {
          return `${wills} added to the collection already booked for that address. Hand them all to the courier.`;
        }
        return this.collectionBookedDryRun
          ? `Collection for ${wills} recorded. No request was sent to The Courier Guy on this server.`
          : `Collection booked for ${wills}. Hand them all to The Courier Guy on the date selected.`;
      },
    },
    watch: {
      searchAdvisorEmail(newVal) {
        if (newVal) {
          this.searchText = newVal;
          this.searchData();
        }
      },
      options: {
        handler() {
          this.loading = true;
          const frontendSortColumns = ['fullName'];
          if (
            this.options.sortBy &&
            frontendSortColumns.includes(this.options.sortBy[0])
          ) {
            this.$nextTick(() => {
              this.$apollo.queries.allTestators.refetch();
            });
          }
        },
        deep: true,
      },
      activeTab: {
        handler() {
          this.options.page = 1;
          this.loading = true;
          this.$apollo.queries.allTestators.refetch();
        },
      },
      actionCounts: {
        handler(newCounts) {
          this.$emit('update:counts', newCounts);
        },
        immediate: true,
      },
    },
    methods: {
      redirectToSummary(item) {
        this.$router.push('advisors/will-summary/' + item.id);
      },
      getActionColor(item) {
        switch (item.nextRequiredAction) {
          case 'Send Will For Storage':
            return 'teal';
          case 'Download Will':
            return 'blue';
          case 'Sign And Upload Will':
            return 'orange';
          case 'Complete Will Steps':
            return 'pink';
          default:
            return 'grey';
        }
      },
      getActionIcon(action) {
        switch (action) {
          case 'Send Will For Storage':
            return 'green.svg';
          case 'Download Will':
            return 'blue.svg';
          case 'Sign And Upload Will':
            return 'orange.svg';
          case 'Complete Will Steps':
            return 'pink.svg';
          case 'In Storage':
            return 'green.svg';
          default:
            return 'green.svg';
        }
      },
      searchData() {
        this.searchQueryParam = this.searchText;
        this.options.page = 1;
        this.loading = true;
        this.$apollo.queries.allTestators.refetch();
      },
      clearSearch() {
        this.searchText = '';
        this.searchQueryParam = null;
        this.options.page = 1;
        this.loading = true;
        this.$apollo.queries.allTestators.refetch();
      },
      queryVariables() {
        const limit = this.options.itemsPerPage || 10;
        const page = this.options.page || 1;
        const search = this.searchQueryParam || null;
        let sortBy = null;
        let sortDesc = true;
        if (this.options.sortBy && this.options.sortBy.length) {
          sortBy = this.options.sortBy[0];
          sortDesc = this.options.sortDesc[0];
          const frontendSortColumns = ['fullName'];
          if (frontendSortColumns.includes(sortBy)) {
            sortBy = null;
            sortDesc = true;
          }
        }
        return {
          page,
          limit,
          search,
          sortBy,
          sortDesc,
          activeTab: this.activeTab,
        };
      },
      onTabSelect(val) {
        this.$emit('update:active-tab', val);
      },
      openCollectionDialog() {
        this.collectionDialogOpen = true;
      },
      onCollectionBooked(result) {
        this.collectionBookedDryRun = result && result.dryRun === true;
        this.collectionBookedLinked = !!(result && result.linked);
        this.$apollo.queries.eligibleCollectionTestators.refetch();
        if (result && result.dryRunRequests) {
          this.dryRunPreviewRequests = result.dryRunRequests;
          // The booking dialog is closing in this same tick; opening the
          // preview now would stack its backdrop above itself.
          whenNoDialogIsActive(() => {
            this.dryRunPreviewOpen = true;
          });
        }
        const bookedIds = (result && result.bookedTestatorIds) || [];
        this.collectionBookedCount = bookedIds.length;
        this.collectionBookedSnackbar = true;
        const bookedIdSet = new Set(bookedIds.map(String));
        this.allTestators.forEach(row => {
          if (bookedIdSet.has(String(row.id))) {
            this.$set(row, 'willReadyToCollect', true);
          }
        });
      },
    },
    apollo: {
      eligibleCollectionTestators: {
        query: ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
        update: data => data.advisorCollectionEligibleTestators || [],
        fetchPolicy: 'network-only',
        error(error) {
          console.error('Error fetching wills ready for collection:', error);
        },
      },
      allTestators: {
        query: ADVISOR_TESTATORS_OPTIMIZED_QUERY,
        variables() {
          return this.queryVariables();
        },
        fetchPolicy: 'cache-and-network',
        result(response) {
          if (response.data) {
            this.totalTestators =
              response.data.advisorTestatorsOptimized.totalTestators;
          }
          this.loading = false;
        },
        error(error) {
          console.error('Apollo query error:', error);
          this.loading = false;
        },
        update: data => data.advisorTestatorsOptimized.testators,
        watchLoading(isLoading) {
          this.loading = isLoading;
        },
      },
      testatorActionCounts: {
        query: ADVISOR_TESTATOR_ACTION_COUNTS_OPTIMIZED_QUERY,
        update: data => data.advisorTestatorActionCountsOptimized,
        fetchPolicy: 'cache-and-network',
        result(response) {
          if (
            response.data &&
            response.data.advisorTestatorActionCountsOptimized
          ) {
            const c = response.data.advisorTestatorActionCountsOptimized;
            this.actionCountsData = {
              complete_steps: c.completeSteps,
              download_wills: c.downloadWills,
              sign_upload_wills: c.signUploadWills,
              validated_wills: c.validatedWills,
              in_storage: c.inStorage,
            };
          }
        },
        error(error) {
          console.error('advisorTestatorActionCountsOptimized error:', error);
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .v-card {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .mobile-stage-select {
    padding: 0 24px 0 24px;
  }

  .action-row {
    display: flex;
    align-items: center;
    height: 48px;
    padding-left: 8px;
    gap: 8px;

    .icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    .action-text {
      font-size: 14px;
      font-weight: 400;
      line-height: 19px;
      letter-spacing: 0px;
      vertical-align: middle;
      color: #333333;
      display: flex;
      align-items: center;
      height: 20px;
    }
  }

  ::v-deep .v-data-table {
    table {
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
    }
    thead tr th {
      border-bottom: 1px solid #e0e0e0 !important;
      border-right: 1px solid #e0e0e0 !important;
      &:last-child {
        border-right: none !important;
      }
    }
    tbody tr {
      border-bottom: 1px solid #e0e0e0;
      &:last-child {
        border-bottom: none;
      }
      &:hover {
        background-color: #f5f5f5 !important;
      }
    }
    tbody tr td {
      border-right: 1px solid #e0e0e0;
      &:last-child {
        border-right: none;
      }
      font-family: Montserrat !important;
      font-weight: 400 !important;
      font-size: 14px !important;
      line-height: 19px !important;
      letter-spacing: 0px !important;
      vertical-align: middle !important;
      padding: 0 16px !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    th {
      background: #f1f2f2 !important;
      font-family: Montserrat !important;
      font-weight: 600 !important;
      font-size: 14px !important;
      line-height: 19px !important;
      letter-spacing: 0px !important;
      vertical-align: middle !important;
      color: #222 !important;
    }
  }

  .email-link {
    color: inherit;
    text-decoration: none;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  ::v-deep .v-data-table td,
  ::v-deep .v-data-table th {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: break-word;
  }
</style>
